import { API_BASE } from '../../lib/config';
/**
 * NotificationBell - In-app notification bell with dropdown
 * Shows unread count badge and notification list
 */

import React, { useState } from 'react';
import useNotificationInbox from '../../hooks/useNotificationInbox';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Bell, Check, CheckCheck, Trash2, MessageSquare, Calendar, Trophy, Users, X, Shirt, ClipboardList } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { toast } from 'sonner';

const API_URL = API_BASE;

// Notification type icons and colors
const getNotificationMeta = (type) => {
  switch (type) {
    case 'chat_message':
    case 'chat_dm':
    case 'chat_leader':
      return { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-500/10' };
    case 'event_new':
    case 'event_reminder':
    case 'attendance_reminder':
      return { icon: Calendar, color: 'text-green-500', bg: 'bg-green-500/10' };
    case 'squad_selection':
      return { icon: Shirt, color: 'text-emerald-500', bg: 'bg-emerald-500/10' };
    case 'evaluation_request':
    case 'evaluation':
      return { icon: ClipboardList, color: 'text-amber-500', bg: 'bg-amber-500/10' };
    case 'praise_received':
      return { icon: Trophy, color: 'text-yellow-500', bg: 'bg-yellow-500/10' };
    case 'team_update':
      return { icon: Users, color: 'text-purple-500', bg: 'bg-purple-500/10' };
    default:
      return { icon: Bell, color: 'text-muted-foreground', bg: 'bg-muted' };
  }
};

// Format relative time
const formatRelativeTime = (dateString, t, lang) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('notifications:state.just_now');
  if (diffMins < 60) return t('notifications:state.minutes_ago', { count: diffMins });
  if (diffHours < 24) return t('notifications:state.hours_ago', { count: diffHours });
  if (diffDays < 7) return t('notifications:state.days_ago', { count: diffDays });
  
  return date.toLocaleDateString(lang === 'en' ? 'en-GB' : 'sv-SE', { day: 'numeric', month: 'short' });
};

export default function NotificationBell() {
  const { t, i18n } = useTranslation(['notifications']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const {
    notifications, unreadCount, readCount, unreadOnly, setUnreadOnly,
    loading, error, busy, refresh, hasMore, loadMore,
    markRead, markAllRead, remove, clearRead,
  } = useNotificationInbox(open);
  const handleMarkRead = (id, event) => { event?.stopPropagation(); return markRead(id); };
  const handleMarkAllRead = () => markAllRead();
  const handleDelete = (id, event) => { event?.stopPropagation(); return remove(id); };

  // Handle notification click - navigate to relevant page
  const handleNotificationClick = (notification) => {
    // Mark as read first
    if (!notification.read) {
      handleMarkRead(notification.id);
    }
    
    // Navigate based on notification type and data
    const { data, type } = notification;

    // Generic deep-link: prefer the explicit stored URL when present.
    const storedUrl = notification.url && notification.url !== '/' ? notification.url : notification.data?.url;
    if (storedUrl && storedUrl.startsWith('/') && !storedUrl.startsWith('//')) {
      navigate(storedUrl);
      setOpen(false);
      return;
    }

    if (type === 'chat_message' || type === 'chat_dm' || type === 'chat_leader') {
      if (data?.channel_id) {
        navigate(`/chat?channel=${data.channel_id}`);
      } else {
        navigate('/chat');
      }
    } else if (type === 'event_new' || type === 'event_reminder') {
      if (data?.event_id) {
        navigate(`/calendar?event=${data.event_id}`);
      } else {
        navigate('/calendar');
      }
    } else if (type === 'praise_received') {
      navigate('/praise');
    } else if (type === 'team_update') {
      navigate('/team');
    }
    
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1.5 text-xs"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        align="end"
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold">{t('notifications:titles.notifications')}</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-xs"
              onClick={handleMarkAllRead}
              disabled={busy}
            >
              <CheckCheck className="w-4 h-4 mr-1" />
              {t('notifications:actions.mark_all_read')}
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <button className={`px-3 min-h-9 rounded-full text-xs ${!unreadOnly ? 'bg-primary/15 text-primary' : 'text-muted-foreground'}`} aria-pressed={!unreadOnly} onClick={() => setUnreadOnly(false)}>Alla</button>
          <button className={`px-3 min-h-9 rounded-full text-xs ${unreadOnly ? 'bg-primary/15 text-primary' : 'text-muted-foreground'}`} aria-pressed={unreadOnly} onClick={() => setUnreadOnly(true)}>Olästa</button>
          <button disabled={busy || readCount === 0} onClick={clearRead} className="ml-auto min-h-9 px-2 text-xs text-muted-foreground disabled:opacity-40">Rensa lästa</button>
        </div>
        {error && <button onClick={refresh} className="w-full p-3 text-xs text-[rgb(var(--tn-text-f87171))]">Kunde inte hämta notiser. Försök igen.</button>}
        {/* Notification list */}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>{t('notifications:state.no_notifications')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => {
                const meta = getNotificationMeta(notification.type);
                const IconComponent = meta.icon;
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-muted/50 cursor-pointer transition-colors ${
                      !notification.read ? 'bg-primary/5' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      {/* Icon */}
                      <div className={`w-9 h-9 rounded-full ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`w-4 h-4 ${meta.color}`} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium truncate ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {notification.body}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatRelativeTime(notification.created_at, t, i18n.language)}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        {!notification.read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={busy}
                            onClick={(e) => handleMarkRead(notification.id, e)}
                            title={t('notifications:actions.mark_read')}
                          >
                            <Check className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          disabled={busy}
                          onClick={(e) => handleDelete(notification.id, e)}
                          title={t('notifications:actions.clear_all').replace(/all/gi,'').trim() || 'Delete'}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {hasMore && <button disabled={loading} onClick={loadMore} className="w-full min-h-11 text-sm text-primary">Visa fler</button>}
        </ScrollArea>
        
        {/* Footer */}
        <div className="p-3 border-t border-border">
          <Button 
            variant="ghost" 
            className="w-full text-sm"
            onClick={() => {
              navigate('/settings/notifications');
              setOpen(false);
            }}
          >
            {t('notifications:titles.notification_settings')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
